import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { Smartphone, RefreshCw, LogOut, CheckCircle2, XCircle, Loader2 } from "lucide-react";

const STATE_INFO = {
    ready: ["Collegato", "text-green-300", CheckCircle2],
    authenticated: ["Autenticazione...", "text-amber-300", Loader2],
    qr: ["In attesa di scansione", "text-amber-300", Loader2],
    initializing: ["Avvio in corso...", "text-amber-300", Loader2],
    disconnected: ["Non collegato", "text-ink-400", XCircle],
    unavailable: ["Servizio non disponibile", "text-red-300", XCircle],
};

export default function WhatsAppConnect({ onClose }) {
    const [status, setStatus] = useState({ state: "loading" });
    const [busy, setBusy] = useState(false);
    const pollRef = useRef(null);

    const load = async () => {
        try { const r = await api.get("/crm/whatsapp/status"); setStatus(r.data); } catch { setStatus({ state: "unavailable" }); }
    };
    useEffect(() => {
        load();
        pollRef.current = setInterval(load, 3000);
        return () => clearInterval(pollRef.current);
    }, []);

    const connect = async () => {
        setBusy(true);
        try { await api.post("/crm/whatsapp/connect"); await load(); }
        catch (e) { alert(e?.response?.data?.detail || "Servizio non disponibile"); }
        finally { setBusy(false); }
    };
    const logout = async () => {
        if (!window.confirm("Scollegare il numero WhatsApp?")) return;
        setBusy(true);
        try { await api.post("/crm/whatsapp/logout"); await load(); } finally { setBusy(false); }
    };

    const [label, color, Icon] = STATE_INFO[status.state] || ["Caricamento...", "text-ink-400", Loader2];
    const connected = status.state === "ready";

    return (
        <div className="fixed inset-0 z-[10060] bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
            <div data-testid="wa-connect-modal" className="bg-ink-950 border border-white/15 max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h4 className="font-serif text-xl text-ink-50 flex items-center gap-2"><Smartphone size={18} className="text-green-300" /> Connessione WhatsApp Web</h4>
                    <button data-testid="wa-connect-close" onClick={onClose} className="text-ink-400 hover:text-ink-50">✕</button>
                </div>
                <div className="mb-4 flex items-center gap-2">
                    <span className="text-[8px] uppercase px-1.5 py-0.5 border border-amber-600/50 text-amber-300">Integrazione non ufficiale</span>
                    <span data-testid="wa-connect-state" className={`text-xs flex items-center gap-1.5 ${color}`}><Icon size={13} className={label.includes("...") || status.state === "qr" ? "animate-spin" : ""} /> {label}</span>
                </div>

                {connected ? (
                    <div className="text-center py-4">
                        <CheckCircle2 size={36} className="mx-auto text-green-400 mb-3" />
                        <div className="text-sm text-ink-50">Numero collegato</div>
                        {status.phone && <div data-testid="wa-connected-phone" className="text-xs text-ink-400 font-mono mt-1">+{status.phone}</div>}
                        <button data-testid="wa-logout-btn" onClick={logout} disabled={busy} className="mt-5 px-4 py-2 border border-red-700/40 text-red-300 hover:bg-red-900/20 text-xs flex items-center gap-2 mx-auto"><LogOut size={12} /> Scollega numero</button>
                    </div>
                ) : status.state === "qr" && status.qr ? (
                    <div className="text-center">
                        <p className="text-xs text-ink-400 mb-3">Apri WhatsApp sul telefono → <b className="text-ink-100">Impostazioni → Dispositivi collegati → Collega dispositivo</b> e inquadra il QR:</p>
                        <img data-testid="wa-qr-image" src={status.qr} alt="QR WhatsApp" className="w-52 h-52 mx-auto bg-white p-2" />
                        <p className="text-[10px] text-ink-500 mt-3">Il QR si aggiorna automaticamente. Consigliato un numero secondario dedicato.</p>
                    </div>
                ) : status.state === "unavailable" ? (
                    <div className="text-center py-6">
                        <XCircle size={32} className="mx-auto text-red-300 mb-3" />
                        <p className="text-xs text-ink-400">{status.note || "Il servizio WhatsApp Web non è attivo in questo ambiente."}</p>
                    </div>
                ) : (
                    <div className="text-center py-4">
                        <p className="text-xs text-ink-400 mb-4">Collega il numero WhatsApp dell&apos;agenzia per abilitare l&apos;invio automatico. Verrà generato un QR code da scansionare.</p>
                        <button data-testid="wa-connect-btn" onClick={connect} disabled={busy} className="cta-gold px-5 py-2.5 text-xs flex items-center gap-2 mx-auto">
                            {busy ? <><Loader2 size={13} className="animate-spin" /> Avvio...</> : <><RefreshCw size={13} /> Collega WhatsApp</>}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
